import type { BuildChartContext } from '../catalog/types';
import type { ChartsLegendProps } from '@mui/x-charts/ChartsLegend';
import type { Theme } from '@mui/material/styles';

export const CHART_FONT_FAMILY = 'Archivo, Arial, sans-serif';

export const CHART_DONUT_STYLE = {
  cornerRadius: 6,
  dashboardCornerRadius: 4,
  paddingAngle: 2,
  dashboardPaddingAngle: 3,
  arcLabelMinAngle: 18,
  highlightAdditionalRadius: 6,
  dashboardHighlightAdditionalRadius: 3,
} as const;

export const CHART_AXIS_LABEL_MAX_LENGTH = {
  dashboard: 18,
  details: 32,
} as const;

export function getChartLegendSlotProps(): {
  legend: Pick<ChartsLegendProps, 'direction' | 'sx'>;
} {
  return {
    legend: {
      direction: 'horizontal',
      sx: {
        fontFamily: CHART_FONT_FAMILY,
        typography: 'body2',
        '& .MuiChartsLegend-mark': {
          rx: 2,
        },
      },
    },
  };
}

export function getChartAxisSlotProps(variant: BuildChartContext['variant']): {
  tickLabelStyle: {
    fontFamily: string;
    fontSize: number;
    fill: string;
  };
} {
  return {
    tickLabelStyle: {
      fontFamily: CHART_FONT_FAMILY,
      fontSize: variant === 'dashboard' ? 11 : 12,
      fill: 'currentColor',
    },
  };
}

const CHART_TOOLTIP_TEXT_SX = {
  whiteSpace: 'nowrap',
  fontSize: '0.75rem',
  lineHeight: 1.3,
} as const;

export function getChartTooltipSx(): {
  '& .MuiChartsTooltip-paper': { maxWidth: string; width: string };
  '& .MuiChartsTooltip-table': { width: string };
  '& caption': { whiteSpace: 'nowrap' };
  '& .MuiChartsTooltip-cell.MuiChartsTooltip-labelCell': typeof CHART_TOOLTIP_TEXT_SX;
  '& .MuiChartsTooltip-cell.MuiChartsTooltip-valueCell': typeof CHART_TOOLTIP_TEXT_SX;
} {
  return {
    '& .MuiChartsTooltip-paper': {
      maxWidth: 'max-content',
      width: 'max-content',
    },
    '& .MuiChartsTooltip-table': {
      width: 'max-content',
    },
    '& caption': {
      whiteSpace: 'nowrap',
    },
    '& .MuiChartsTooltip-cell.MuiChartsTooltip-labelCell': CHART_TOOLTIP_TEXT_SX,
    '& .MuiChartsTooltip-cell.MuiChartsTooltip-valueCell': CHART_TOOLTIP_TEXT_SX,
  };
}

export function getChartGridColor(theme: Theme): string {
  return theme.palette.divider;
}

export function getChartAreaSx(): {
  width: string;
  flex: number;
  minHeight: number;
  display: string;
  alignItems: string;
  justifyContent: string;
} {
  return {
    width: '100%',
    flex: 1,
    minHeight: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
}

/** Dashboard cards: horizontally centered chart, directly under the card heading. */
export function getDashboardChartAreaSx(): {
  width: string;
  display: string;
  flexDirection: string;
  alignItems: string;
  justifyContent: string;
} {
  return {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
  };
}
